import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, FileText, Send, CheckCircle2, History,
  Printer, Download, Eye as EyeIcon, Upload, X, RotateCcw, Bike,
  MessageCircle, PhoneCall, Banknote,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import {
  locGet, locGenerate, locLetterBlob, locMarkSent, locResume, locDeliver, locProofBlob,
  locWhatsappSent, locFollowup, locTatSettings,
} from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { LocWhatsappModal } from "@/components/LocWhatsappModal";
import { LocMarkWhatsappSentDialog } from "@/components/LocMarkWhatsappSentDialog";
import { LocFollowupDialog } from "@/components/LocFollowupDialog";

const REASON_LABELS = {
  vehicle_ready: "Vehicle Ready – Customer Not Taking Delivery",
  no_work_started: "No Work Started – Customer Not Responding",
};
const NO_WORK_REASON_LABELS = {
  estimate_approval_pending: "Estimate Approval Pending",
  customer_approval_pending: "Customer Approval Pending",
  customer_instruction_pending: "Customer Instruction Pending",
  insurance_approval_pending: "Insurance Approval Pending",
  other: "Other",
};
const DISPATCH_MODES = ["Registered Post with AD", "Speed Post", "Courier", "Hand Delivery", "Other"];
const GENERATE_FROM_STAGE = { 1: "awaiting_loc1", 2: "loc2_due", 3: "loc3_due" };
const GENERATED_STAGE = { 1: "loc1_generated", 2: "loc2_generated", 3: "loc3_generated" };

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

const Section = ({ title, icon, children }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-lg p-4 space-y-3">
    <div className="text-[13.5px] font-semibold text-[#1a1b22] flex items-center gap-2">{icon}{title}</div>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div>
    <div className="text-[10.5px] font-semibold uppercase tracking-wider text-[#747684]">{label}</div>
    <div className="text-[13px] text-[#1a1b22] mt-0.5">{value ?? "—"}</div>
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{children}</div>;

const PARKING_STATUS_LABELS = {
  scheduled: ["Scheduled", "bg-sky-50 text-sky-700 border-sky-200"],
  active: ["Parking Charges Active", "bg-rose-50 text-rose-700 border-rose-200"],
  stopped: ["Stopped — Repair Resumed", "bg-[#f4f3fc] text-[#747684] border-[#e2e1eb]"],
  final: ["Final — Vehicle Delivered", "bg-[#f4f3fc] text-[#747684] border-[#e2e1eb]"],
};

/** Compact, read-only operational estimate — no invoice/billing is created. */
const ParkingChargesPanel = ({ caseObj }) => {
  const [label, tone] = PARKING_STATUS_LABELS[caseObj.parking_status] || ["—", "bg-[#f4f3fc] text-[#747684] border-[#e2e1eb]"];
  return (
    <Section title="Parking Charges" icon={<Banknote className="w-4 h-4 text-yamaha-700" />}>
      <div className="flex items-center justify-between">
        <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${tone}`} data-testid="loc-parking-status-badge">{label}</span>
        <span className="text-[11.5px] text-[#747684]">Estimate only — not an invoice.</span>
      </div>
      <Grid>
        <Row label="Start Date" value={fmtDate(caseObj.parking_charge_start_date)} />
        <Row label="Rate" value={caseObj.parking_charge_per_day ? `₹${caseObj.parking_charge_per_day}/day` : "—"} />
        <Row label="Chargeable Days" value={caseObj.parking_chargeable_days ?? 0} />
        <Row label="Estimated Charges" value={`₹${caseObj.parking_estimated_charges ?? 0}`} />
        {caseObj.parking_charge_end_date && <Row label="Charge End Date" value={fmtDate(caseObj.parking_charge_end_date)} />}
      </Grid>
    </Section>
  );
};

/** Opens an authenticated PDF blob for preview (new tab), print (hidden
 *  iframe + window.print), or download (temporary <a download>). */
const runLetterAction = async (fetcher, filename, mode) => {
  const blob = await fetcher();
  const url = URL.createObjectURL(blob);
  if (mode === "download") {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  } else if (mode === "print") {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    document.body.appendChild(iframe);
    iframe.onload = () => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ }
    };
    iframe.src = url;
    setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 60000);
  } else {
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
};

/** Local (browser-timezone) yyyy-mm-dd — avoids the off-by-one that
 *  `toISOString()` (UTC) can cause for IST users near midnight. */
const ymdLocal = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const suggestedParkingDate = (graceDays) => {
  const d = new Date();
  d.setDate(d.getDate() + (graceDays ?? 5));
  return ymdLocal(d);
};

const StageCard = ({ caseObj, stageNum, canWrite, parkingEnabled, parkingGraceDays, onGenerate, onLetterAction, onOpenMarkSent, onViewProof }) => {
  const rec = (caseObj.locs || []).find((l) => l.stage === stageNum) || {};
  const generated = !!rec.generated_at;
  const sent = !!rec.sent_at;
  const isDueStage = caseObj.status === "active" && caseObj.stage === GENERATE_FROM_STAGE[stageNum];
  const canGenerate = canWrite && isDueStage;
  const canSend = canWrite && caseObj.status === "active" && generated && !sent
    && caseObj.stage === GENERATED_STAGE[stageNum];
  const showParkingDateInput = stageNum === 2 && parkingEnabled && !caseObj.parking_charge_start_date;
  const [parkingDate, setParkingDate] = useState(() => suggestedParkingDate(parkingGraceDays));
  const [parkingDateTouched, setParkingDateTouched] = useState(false);
  useEffect(() => {
    if (!parkingDateTouched) setParkingDate(suggestedParkingDate(parkingGraceDays));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingGraceDays]);

  let statusLabel = "Not yet due", statusTone = "bg-[#f4f3fc] text-[#747684] border-[#e2e1eb]";
  if (sent) { statusLabel = "Sent"; statusTone = "bg-emerald-50 text-emerald-700 border-emerald-200"; }
  else if (generated) { statusLabel = "Generated — not sent"; statusTone = "bg-amber-50 text-amber-700 border-amber-200"; }
  else if (isDueStage) { statusLabel = "Ready to generate"; statusTone = "bg-sky-50 text-sky-700 border-sky-200"; }

  const btnCls = "px-2.5 py-1 rounded-md text-[11px] font-semibold border border-[#e2e1eb] text-[#444653] hover:bg-[#faf8ff] inline-flex items-center gap-1";

  return (
    <div className="border border-[#e2e1eb] rounded-lg p-3 space-y-2" data-testid={`loc-stage-card-${stageNum}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-[13px] text-[#1a1b22]">LOC {stageNum}</div>
        <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${statusTone}`}>{statusLabel}</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {!generated && canGenerate && showParkingDateInput && (
          <div className="w-full bg-amber-50 border border-amber-200 rounded-md p-2 space-y-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 block">
              Parking Charges Applicable From
            </label>
            <input type="date" data-testid="loc-parking-start-date-input" value={parkingDate}
              onChange={(e) => { setParkingDate(e.target.value); setParkingDateTouched(true); }}
              className="w-full border border-amber-200 rounded-md px-2 py-1 text-[12px] focus:outline-none focus:border-amber-400" />
          </div>
        )}
        {!generated && canGenerate && (
          <button onClick={() => onGenerate(stageNum, showParkingDateInput ? parkingDate : undefined)} data-testid={`loc-generate-btn-${stageNum}`}
            className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-yamaha-700 hover:bg-yamaha-800 inline-flex items-center gap-1">
            <FileText className="w-3 h-3" /> Generate LOC {stageNum}
          </button>
        )}
        {generated && (
          <>
            <button onClick={() => onLetterAction(stageNum, "preview")} data-testid={`loc-preview-btn-${stageNum}`} className={btnCls}>
              <EyeIcon className="w-3 h-3" /> Preview
            </button>
            <button onClick={() => onLetterAction(stageNum, "print")} data-testid={`loc-print-btn-${stageNum}`} className={btnCls}>
              <Printer className="w-3 h-3" /> Print
            </button>
            <button onClick={() => onLetterAction(stageNum, "download")} data-testid={`loc-download-btn-${stageNum}`} className={btnCls}>
              <Download className="w-3 h-3" /> Download PDF
            </button>
            {canSend && (
              <button onClick={() => onOpenMarkSent(stageNum)} data-testid={`loc-mark-sent-btn-${stageNum}`}
                className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-1">
                <Send className="w-3 h-3" /> Mark as Sent
              </button>
            )}
          </>
        )}
      </div>

      {sent && (
        <div className="bg-[#fafafd] border border-[#e2e1eb] rounded-md p-2 text-[11.5px] text-[#444653] space-y-0.5">
          <div>Dispatched: <b>{fmtDate(rec.dispatch_date)}</b> via <b>{rec.dispatch_mode}</b></div>
          {rec.docket_number && <div>Docket/Consignment No.: <b>{rec.docket_number}</b></div>}
          {rec.dispatch_remarks && <div>Remarks: {rec.dispatch_remarks}</div>}
          <div className="text-[10.5px] text-[#747684]">Marked sent by {rec.sent_by} · {fmtDateTime(rec.sent_at)}</div>
          {rec.proof_gridfs_id && (
            <button onClick={() => onViewProof(stageNum)} data-testid={`loc-view-proof-btn-${stageNum}`}
              className="mt-1 inline-flex items-center gap-1 text-yamaha-700 font-semibold hover:underline">
              <Upload className="w-3 h-3" /> View Dispatch Proof
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const MarkSentDialog = ({ stageNum, onClose, onSubmit }) => {
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState(DISPATCH_MODES[0]);
  const [docket, setDocket] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit(stageNum, { dispatchDate, mode, docket, remarks, file });
      onClose();
    } catch (e) {
      // Error toast already shown by the caller; keep the dialog open to retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-mark-sent-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-sm shadow-xl">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-[#1a1b22]">Mark LOC {stageNum} as Sent</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#747684]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Dispatch Date *</label>
            <input type="date" data-testid="loc-sent-date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Dispatch Mode *</label>
            <select data-testid="loc-sent-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>
              {DISPATCH_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Docket / Consignment No.</label>
            <input data-testid="loc-sent-docket" value={docket} onChange={(e) => setDocket(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Remarks</label>
            <textarea data-testid="loc-sent-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Dispatch Proof (optional)</label>
            <input type="file" data-testid="loc-sent-proof-file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-[12px]" accept="image/*,.pdf" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="loc-sent-submit-btn"
            className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60">
            {saving ? "Saving…" : "Save & Mark Sent"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ResumeDialog = ({ onClose, onSubmit }) => {
  const [responseDate, setResponseDate] = useState(new Date().toISOString().slice(0, 10));
  const [instruction, setInstruction] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ responseDate, instruction, remarks }); onClose(); } catch (e) { /* error toast shown by caller */ } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-resume-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-sm shadow-xl">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-[#1a1b22]">Resume Repair</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#747684]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Customer Response Date</label>
            <input type="date" data-testid="loc-resume-date" value={responseDate} onChange={(e) => setResponseDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Customer Instruction / Approval</label>
            <textarea data-testid="loc-resume-instruction" value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Remarks</label>
            <textarea data-testid="loc-resume-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="loc-resume-submit-btn"
            className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60">
            {saving ? "Saving…" : "Resume Repair"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeliverDialog = ({ onClose, onSubmit }) => {
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ deliveryDate, remarks }); onClose(); } catch (e) { /* error toast shown by caller */ } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="loc-deliver-modal">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-sm shadow-xl">
        <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-[#1a1b22]">Vehicle Delivered / Close LOC</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#747684]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Delivery Date</label>
            <input type="date" data-testid="loc-deliver-date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Remarks</label>
            <textarea data-testid="loc-deliver-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} />
          </div>
          <p className="text-[11px] text-[#747684]">This only closes the LOC Case. Complete the normal PV/AV delivery workflow separately if not already done.</p>
        </div>
        <div className="px-5 py-3 border-t border-[#e2e1eb] flex justify-end gap-2 bg-[#fafafd] rounded-b-xl">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md text-[13px] font-medium text-[#444653] hover:bg-[#f0eff7]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="loc-deliver-submit-btn"
            className="px-4 py-1.5 rounded-md text-[13px] font-semibold text-white bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-60">
            {saving ? "Saving…" : "Close LOC Case"}
          </button>
        </div>
      </div>
    </div>
  );
};

const LocCaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  const canWrite = can(currentUser, "loc_write");

  const [caseObj, setCaseObj] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sentDialogStage, setSentDialogStage] = useState(null);
  const [showResume, setShowResume] = useState(false);
  const [showDeliver, setShowDeliver] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [showMarkWaSent, setShowMarkWaSent] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [workshopContact, setWorkshopContact] = useState("");
  const [parkingPerDay, setParkingPerDay] = useState("");
  const [parkingEnabled, setParkingEnabled] = useState(true);
  const [parkingGraceDays, setParkingGraceDays] = useState(5);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await locGet(id);
      setCaseObj(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load LOC case");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    locTatSettings().then((s) => {
      setWorkshopContact(s.workshop_contact_number || "");
      setParkingPerDay(s.parking_charge_per_day || "");
      setParkingEnabled(s.parking_enabled !== false);
      setParkingGraceDays(s.parking_grace_period_days ?? 5);
    }).catch(() => {});
  }, []);

  if (loading) return <div className="p-8 text-center text-[#747684]">Loading…</div>;
  if (!caseObj) return <div className="p-8 text-center text-[#747684]">LOC Case not found.</div>;

  const handleGenerate = async (stageNum, parkingChargeStartDate) => {
    try {
      const body = { actor: currentUser?.name, actor_role: currentUser?.role };
      if (stageNum === 2 && parkingChargeStartDate) body.parking_charge_start_date = parkingChargeStartDate;
      const updated = await locGenerate(id, stageNum, body);
      setCaseObj(updated);
      toast.success(`LOC ${stageNum} generated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate LOC letter");
    }
  };

  const handleLetterAction = async (stageNum, mode) => {
    try {
      await runLetterAction(() => locLetterBlob(id, stageNum), `${caseObj.loc_number}_LOC${stageNum}.pdf`, mode);
    } catch (e) {
      toast.error("Failed to open LOC letter");
    }
  };

  const handleMarkSent = async (stageNum, { dispatchDate, mode, docket, remarks, file }) => {
    try {
      const fd = new FormData();
      fd.append("dispatch_date", dispatchDate);
      fd.append("dispatch_mode", mode);
      if (docket) fd.append("docket_number", docket);
      if (remarks) fd.append("remarks", remarks);
      if (file) fd.append("proof_file", file);
      fd.append("actor", currentUser?.name || "");
      fd.append("actor_role", currentUser?.role || "");
      const updated = await locMarkSent(id, stageNum, fd);
      setCaseObj(updated);
      toast.success(`LOC ${stageNum} marked as sent.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark LOC as sent");
      throw e;
    }
  };

  const handleViewProof = async (stageNum) => {
    try {
      const blob = await locProofBlob(id, stageNum);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("Failed to load dispatch proof");
    }
  };

  const handleResume = async ({ responseDate, instruction, remarks }) => {
    try {
      const updated = await locResume(id, {
        customer_response_date: responseDate, customer_instruction: instruction, remarks,
        actor: currentUser?.name, actor_role: currentUser?.role,
      });
      setCaseObj(updated);
      toast.success("Repair resumed. LOC Case closed; vehicle returned to normal workflow.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resume repair");
      throw e;
    }
  };

  const handleDeliver = async ({ deliveryDate, remarks }) => {
    try {
      const updated = await locDeliver(id, {
        delivery_date: deliveryDate, remarks, actor: currentUser?.name, actor_role: currentUser?.role,
      });
      setCaseObj(updated);
      toast.success("Vehicle delivered. LOC Case closed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close LOC case");
      throw e;
    }
  };

  const handleWhatsappMarkSent = async (payload) => {
    try {
      const updated = await locWhatsappSent(id, { ...payload, actor: currentUser?.name, actor_role: currentUser?.role });
      setCaseObj(updated);
      toast.success("Marked as WhatsApp Sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log WhatsApp Sent");
      throw e;
    }
  };

  const handleFollowup = async ({ date, mode, remarks }) => {
    try {
      const updated = await locFollowup(id, { date, mode, remarks, actor: currentUser?.name, actor_role: currentUser?.role });
      setCaseObj(updated);
      toast.success("Follow-up added to the timeline.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add follow-up");
      throw e;
    }
  };

  const reasonText = caseObj.loc_reason === "no_work_started"
    ? `${REASON_LABELS[caseObj.loc_reason]} — ${NO_WORK_REASON_LABELS[caseObj.reason_detail] || caseObj.reason_detail}${caseObj.reason_detail === "other" && caseObj.reason_detail_other ? ` (${caseObj.reason_detail_other})` : ""}`
    : REASON_LABELS[caseObj.loc_reason];

  const sourcePath = caseObj.source_type === "pv" ? "/pv/workbench" : `/av/${caseObj.source_id}`;

  return (
    <div className="space-y-3" data-testid="loc-case-detail-page">
      <header className="flex items-start gap-3">
        <button onClick={() => navigate("/loc")} data-testid="loc-back-btn" className="w-9 h-9 rounded-lg border border-[#e2e1eb] flex items-center justify-center hover:bg-[#faf8ff]">
          <ArrowLeft className="w-4 h-4 text-[#444653]" />
        </button>
        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-[18px] font-semibold text-yamaha-800 leading-none tracking-tight">{caseObj.loc_number}</h1>
          <p className="text-[11.5px] text-[#747684] mt-1">{caseObj.customer_name} · {caseObj.registration_number || caseObj.vin_no}</p>
        </div>
        <button onClick={() => navigate(sourcePath)} data-testid="loc-view-source-btn"
          className="px-3 py-1.5 rounded-lg border border-[#e2e1eb] text-[12px] font-semibold text-[#444653] hover:bg-[#faf8ff] inline-flex items-center gap-1.5">
          <Bike className="w-3.5 h-3.5" /> View Source {caseObj.source_type.toUpperCase()}
        </button>
        {canWrite && caseObj.status === "active" && (
          <>
            <button onClick={() => setShowResume(true)} data-testid="loc-resume-btn"
              className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Resume Repair
            </button>
            <button onClick={() => setShowDeliver(true)} data-testid="loc-deliver-btn"
              className="px-3 py-1.5 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Vehicle Delivered / Close
            </button>
          </>
        )}
      </header>

      {caseObj.status !== "active" && (
        <div className={`px-3 py-2 rounded-lg text-[12px] font-semibold border ${
          caseObj.status === "closed" ? "bg-[#f4f3fc] text-[#444653] border-[#e2e1eb]" : "bg-emerald-50 text-emerald-700 border-emerald-200"
        }`}>
          This LOC Case is {caseObj.status === "closed" ? "Closed" : "marked Repair Resumed"} — read-only.
        </div>
      )}

      <Section title="Case Details" icon={<FileText className="w-4 h-4 text-yamaha-700" />}>
        <Grid>
          <Row label="Customer" value={caseObj.customer_name} />
          <Row label="Mobile" value={caseObj.mobile_number} />
          <Row label="Address" value={caseObj.address} />
          <Row label="Vehicle Model" value={caseObj.vehicle_model} />
          <Row label="Registration No." value={caseObj.registration_number} />
          <Row label="Chassis / VIN No." value={caseObj.vin_no} />
          <Row label="Job Card No." value={caseObj.job_card_no} />
          <Row label="AV No." value={caseObj.av_number} />
          <Row label="Location" value={caseObj.location_code} />
          <Row label="Service Advisor" value={caseObj.service_advisor} />
          <Row label="Assigned User" value={caseObj.assigned_user} />
          <Row label="LOC Reason" value={reasonText} />
          <Row label="LOC Trigger Date" value={fmtDate(caseObj.trigger_date)} />
          <Row label="Vehicle Ready Date" value={caseObj.ready_date ? fmtDate(caseObj.ready_date) : "—"} />
          <Row label="Remarks" value={caseObj.remarks} />
        </Grid>
      </Section>

      <Section title="LOC Letters" icon={<Send className="w-4 h-4 text-yamaha-700" />}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[1, 2, 3].map((n) => (
            <StageCard
              key={n} caseObj={caseObj} stageNum={n} canWrite={canWrite}
              parkingEnabled={parkingEnabled} parkingGraceDays={parkingGraceDays}
              onGenerate={handleGenerate} onLetterAction={handleLetterAction}
              onOpenMarkSent={setSentDialogStage} onViewProof={handleViewProof}
            />
          ))}
        </div>
      </Section>

      {caseObj.parking_charge_start_date && <ParkingChargesPanel caseObj={caseObj} />}

      <Section title="Communication" icon={<MessageCircle className="w-4 h-4 text-yamaha-700" />}>
        <p className="text-[11.5px] text-[#747684] -mt-1 mb-1">
          Normal WhatsApp only (no Business API, no auto-send) — short reminder, manually reviewed and sent by you.
          The formal printed LOC letter above remains the official communication.
        </p>
        <div className="flex flex-wrap gap-2">
          {canWrite && caseObj.status === "active" && caseObj.mobile_number && (
            <>
              <button onClick={() => setShowWhatsapp(true)} data-testid="loc-open-whatsapp-btn"
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" /> Open WhatsApp
              </button>
              <button onClick={() => setShowMarkWaSent(true)} data-testid="loc-mark-whatsapp-sent-btn"
                className="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 text-[12px] font-semibold inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark WhatsApp Sent
              </button>
            </>
          )}
          {canWrite && caseObj.status === "active" && (
            <button onClick={() => setShowFollowup(true)} data-testid="loc-add-followup-btn"
              className="px-3 py-1.5 rounded-lg border border-[#e2e1eb] text-[#444653] hover:bg-[#faf8ff] text-[12px] font-semibold inline-flex items-center gap-1.5">
              <PhoneCall className="w-3.5 h-3.5" /> Add Follow-up
            </button>
          )}
          {canWrite && caseObj.status === "active" && !caseObj.mobile_number && (
            <span className="text-[11.5px] text-[#a0a2b0] italic self-center">No mobile number on file — WhatsApp actions unavailable.</span>
          )}
        </div>
      </Section>

      <Section title="Activity Timeline" icon={<History className="w-4 h-4 text-yamaha-700" />}>
        {(caseObj.timeline || []).length === 0 ? (
          <div className="text-[12px] text-[#747684] py-6 text-center border border-dashed border-[#e2e1eb] rounded-lg">No timeline events yet.</div>
        ) : (
          <ol className="relative border-l-2 border-[#ece9f5] pl-4 ml-2 space-y-3" data-testid="loc-timeline-list">
            {[...caseObj.timeline].reverse().map((t, i) => (
              <li key={t.id || i} className="relative">
                <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-yamaha-500 ring-4 ring-yamaha-50" />
                <div className="bg-white border border-[#e2e1eb] rounded-lg p-2.5">
                  <div className="text-[12.5px] font-semibold text-[#1a1b22]">{t.message}</div>
                  <div className="text-[10.5px] text-[#747684] mt-1">
                    {t.actor || "—"}{t.actor_role ? ` (${t.actor_role})` : ""} · {fmtDateTime(t.at)}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {sentDialogStage && (
        <MarkSentDialog stageNum={sentDialogStage} onClose={() => setSentDialogStage(null)} onSubmit={handleMarkSent} />
      )}
      {showResume && <ResumeDialog onClose={() => setShowResume(false)} onSubmit={handleResume} />}
      {showDeliver && <DeliverDialog onClose={() => setShowDeliver(false)} onSubmit={handleDeliver} />}
      {showWhatsapp && (
        <LocWhatsappModal
          caseObj={caseObj} workshopContact={workshopContact} defaultParkingPerDay={parkingPerDay}
          onClose={() => setShowWhatsapp(false)} onMarkSent={handleWhatsappMarkSent}
        />
      )}
      {showMarkWaSent && (
        <LocMarkWhatsappSentDialog caseObj={caseObj} onClose={() => setShowMarkWaSent(false)} onSubmit={handleWhatsappMarkSent} />
      )}
      {showFollowup && <LocFollowupDialog onClose={() => setShowFollowup(false)} onSubmit={handleFollowup} />}
    </div>
  );
};

export default LocCaseDetail;
